import { useEffect, useState } from "react";

import { Linking } from "react-native";

import {
  AnalyticsFieldName,
  ProfileAnalyticsEvent,
  ProfileNotificationSettings,
} from "@/constants/analytics/Analytics";
import { ProfileTestID } from "@/constants/TestID";
import useForegroundPushNotificationHandler from "@/hooks/useForegroundPushNotificationHandler";
import usePushNotifications from "@/hooks/usePushNotifications";
import { useAnalytics } from "@/services/Analytics";
import NotificationSettingsCard from "@/ui/components/notificationSettings/NotificationSettingsCard";
import { ActionBar, Button, FAIconName } from "@/ui/core";
import { VStack } from "@/ui/layout";
import { getChanges } from "@/utils/analytics.util";
import { isWeb } from "@/utils/platform.util";
import { joinTestIDs } from "@/utils/string.util";

interface NotificationSettingsSectionProps extends NotificationSettings {
  onChange: (preferences: NotificationSettings) => void;
}

const baseTestID = ProfileTestID.EditNotificationSettings;

const getChangesForMixpanel = ({
  notifications,
  payload,
}: {
  notifications: NotificationSettings;
  payload: UpdateNotificationSettingsBody;
}) => {
  return getChanges({
    current: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      receiveEmails: payload.receiveNotificationEmails,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      receivePush: payload.receiveNotificationPush,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      receiveSms: payload.receiveNotificationSms,
    },
    previous: notifications,
  });
};

const NotificationSettingsSection = ({
  onChange,
  receiveEmails: isEmailEnabledInitially,
  receivePush: isPushEnabledInitially,
  receiveSms: isSmsEnabledInitially,
}: NotificationSettingsSectionProps) => {
  const { track } = useAnalytics();
  const {
    hasAnsweredPushNotificationPrompt,
    isPushNotificationsDisabled,
    registerForPushNotifications,
  } = usePushNotifications();

  const [isSmsEnabled, setIsSmsEnabled] = useState<boolean>(
    isSmsEnabledInitially,
  );
  const [isPushEnabled, setIsPushEnabled] = useState<boolean>(
    isPushEnabledInitially,
  );
  const [isEmailEnabled, setIsEmailEnabled] = useState<boolean>(
    isEmailEnabledInitially,
  );

  useForegroundPushNotificationHandler({
    isPushEnabled,
    registerForPushNotifications,
  });

  useEffect(() => {
    const notificationSettings: NotificationSettings = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      receiveEmails: isEmailEnabled,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      receivePush: isPushEnabled,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      receiveSms: isSmsEnabled,
    };
    onChange(notificationSettings);
  }, [isSmsEnabled, isPushEnabled, isEmailEnabled, onChange]);

  const handleChange = ({
    setValue,
    type,
  }: {
    setValue: (value: boolean) => void;
    type: string;
  }) => {
    return (isChecked: boolean) => {
      track(ProfileAnalyticsEvent.NotificationSettingChanged, {
        [AnalyticsFieldName.NewValue]: isChecked,
        [AnalyticsFieldName.NotificationType]: type,
      });
      setValue(isChecked);
      if (type === ProfileNotificationSettings.Push && isChecked) {
        void registerForPushNotifications();
      }
    };
  };

  const shouldShowPushNotificationDisabledMessage =
    hasAnsweredPushNotificationPrompt &&
    isPushNotificationsDisabled &&
    isPushEnabled;

  return (
    <VStack gap={24}>
      <NotificationSettingsCard
        isChecked={isSmsEnabled}
        isRecommended
        message="By checking this box, you agree to receive text messages from Equip Health. To cancel, reply STOP or change your app preferences. Msg & data rates may apply."
        onChange={handleChange({
          setValue: setIsSmsEnabled,
          type: ProfileNotificationSettings.Sms,
        })}
        testID={joinTestIDs(baseTestID, "sms")}
        title="Text messages"
      />
      {!isWeb() && (
        <NotificationSettingsCard
          isChecked={isPushEnabled}
          isRecommended
          message="Notifications may include alerts, sounds, and icon badges. These can be configured in your iOS Settings for the Equip Health app."
          onChange={handleChange({
            setValue: setIsPushEnabled,
            type: ProfileNotificationSettings.Push,
          })}
          testID={joinTestIDs(baseTestID, "push")}
          title="Push notifications"
        >
          {shouldShowPushNotificationDisabledMessage ? (
            <VStack
              gap={12}
              testID={joinTestIDs(baseTestID, "push-disabled-container")}
            >
              <ActionBar
                iconName={FAIconName.InfoCircle}
                message="You’ve allowed us to send push notifications, but this device is not currently set up to receive them."
                size="small"
                testID={joinTestIDs(baseTestID, "push-disabled")}
                variant="critical"
              />
              <Button
                leftIcon={FAIconName.Gear}
                onPress={() => {
                  track(ProfileAnalyticsEvent.ManageAppPermissions);
                  void Linking.openSettings();
                }}
                testID={joinTestIDs(baseTestID, "push-disabled-button")}
                title="Manage app permissions"
                variant="secondary"
              />
            </VStack>
          ) : null}
        </NotificationSettingsCard>
      )}
      <NotificationSettingsCard
        isChecked={isEmailEnabled}
        onChange={handleChange({
          setValue: setIsEmailEnabled,
          type: ProfileNotificationSettings.Email,
        })}
        testID={joinTestIDs(baseTestID, "email")}
        title="Email"
      />
    </VStack>
  );
};

export { getChangesForMixpanel };
export type { NotificationSettingsSectionProps };
export default NotificationSettingsSection;
